import { Columns3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { SelectableColumn } from "@/hooks/useColumnSelection";

interface ColumnPickerProps {
  /** All columns that can be shown, in display order */
  columns: SelectableColumn[];
  visibleColumns: SelectableColumn[];
  onColumnVisibleChange: (key: string, visible: boolean) => void;
  onReset: () => void;
  /** Prefix for test ids: `${prefix}-columns-button`, `${prefix}-column-toggle-${key}` */
  testIdPrefix: string;
  size?: "default" | "sm";
}

export function ColumnPicker({
  columns,
  visibleColumns,
  onColumnVisibleChange,
  onReset,
  testIdPrefix,
  size = "default",
}: ColumnPickerProps) {
  const visibleKeys = new Set(visibleColumns.map((c) => c.key));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={size}
          className="w-full sm:w-auto"
          data-testid={`${testIdPrefix}-columns-button`}
        >
          <Columns3 className="h-4 w-4 mr-2" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56 max-h-[70vh] overflow-y-auto"
      >
        <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {columns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.key}
            checked={column.required || visibleKeys.has(column.key)}
            disabled={column.required}
            // Keep the menu open so several columns can be toggled at once
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              onColumnVisibleChange(column.key, checked === true)
            }
            data-testid={`${testIdPrefix}-column-toggle-${column.key}`}
          >
            {column.label}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onReset}>Reset to default</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
